import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, getErrorMessage } from "@/lib/api";
import { exportTravelBonusPDF } from "@/lib/travelBonusPdf";
import CityAutocomplete from "./CityAutocomplete";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, MapPin, Plus, Trash2, Loader2, Calculator, Save, Euro, AlertCircle, FileDown } from "lucide-react";

const ZONE_LABELS = {
  1: "Zone 1 (0-10 km)",
  2: "Zone 2 (10-20 km)",
  3: "Zone 3 (20-30 km)",
  4: "Zone 4 (30-40 km)",
  5: "Zone 5 (40-50 km)"
};
const MONTHS_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const TravelBonusTab = () => {
  const now = new Date();
  const [settings, setSettings] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [rates, setRates] = useState([]); // all rates (default + per-employee)
  const [loading, setLoading] = useState(true);

  // Settings form
  const [hqCity, setHqCity] = useState("");

  // Rate dialog
  const [rateDialog, setRateDialog] = useState(false);
  const [rateForm, setRateForm] = useState({
    start_date: new Date().toISOString().split("T")[0],
    employee_id: "",
    zone1: 0, zone2: 0, zone3: 0, zone4: 0, zone5: 0
  });

  // Compute
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [compute, setCompute] = useState(null);
  const [computeLoading, setComputeLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, rRes, eRes] = await Promise.all([
        api.getTravelBonusSettings(),
        api.getTravelBonusRates(),
        api.getEmployees()
      ]);
      setSettings(sRes.data);
      setRates(rRes.data);
      setEmployees(eRes.data.filter(e => !e.exit_date));
      if (sRes.data?.hq_city) {
        setHqCity(sRes.data.hq_postal_code ? `${sRes.data.hq_city} (${sRes.data.hq_postal_code})` : sRes.data.hq_city);
      }
    } catch { toast.error("Erreur chargement"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runCompute = useCallback(async () => {
    if (!settings?.hq_latitude) return;
    try {
      setComputeLoading(true);
      const params = { year, month };
      if (employeeFilter !== "all") params.employee_id = employeeFilter;
      const res = await api.computeTravelBonuses(params);
      setCompute(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur calcul"));
    } finally { setComputeLoading(false); }
  }, [year, month, employeeFilter, settings]);

  useEffect(() => { runCompute(); }, [runCompute]);

  const handleSaveSettings = async () => {
    if (!hqCity) { toast.error("Ville requise"); return; }
    const m = hqCity.match(/^(.+?)\s*\((\d{5})\)\s*$/);
    if (!m) { toast.error("Sélectionnez une ville dans la liste"); return; }
    try {
      const res = await api.updateTravelBonusSettings({ hq_city: m[1].trim(), hq_postal_code: m[2] });
      setSettings(res.data);
      toast.success("Siège social enregistré");
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const openRateDialog = (prefillEmployee = "") => {
    setRateForm({
      start_date: new Date().toISOString().split("T")[0],
      employee_id: prefillEmployee,
      zone1: 0, zone2: 0, zone3: 0, zone4: 0, zone5: 0
    });
    setRateDialog(true);
  };

  const handleCreateRate = async () => {
    try {
      const payload = {
        start_date: rateForm.start_date,
        employee_id: rateForm.employee_id || null,
        zone1: parseFloat(rateForm.zone1) || 0,
        zone2: parseFloat(rateForm.zone2) || 0,
        zone3: parseFloat(rateForm.zone3) || 0,
        zone4: parseFloat(rateForm.zone4) || 0,
        zone5: parseFloat(rateForm.zone5) || 0
      };
      await api.createTravelBonusRate(payload);
      toast.success("Tarif enregistré");
      setRateDialog(false);
      fetchAll();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const handleDeleteRate = async (id) => {
    if (!window.confirm("Supprimer ce tarif ?")) return;
    try {
      await api.deleteTravelBonusRate(id);
      toast.success("Tarif supprimé");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const defaultRates = rates.filter(r => !r.employee_id);
  const employeeRates = rates.filter(r => r.employee_id);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 3 + i);

  return (
    <div className="space-y-6" data-testid="travel-bonus-tab">
      <Tabs defaultValue="compute">
        <TabsList>
          <TabsTrigger value="compute" data-testid="tb-tab-compute"><Calculator className="h-4 w-4 mr-1" /> Calcul</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tb-tab-settings"><Building2 className="h-4 w-4 mr-1" /> Paramètres</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tb-tab-rates"><Euro className="h-4 w-4 mr-1" /> Grilles tarifaires</TabsTrigger>
        </TabsList>

        {/* ---------- COMPUTE ---------- */}
        <TabsContent value="compute" className="space-y-4">
          {!settings?.hq_latitude ? (
            <Card className="border-amber-400 bg-amber-50">
              <CardContent className="pt-6 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <p className="font-medium text-amber-900">Configuration requise</p>
                  <p className="text-sm text-amber-800 mt-1">
                    Veuillez configurer l'adresse du siège social dans l'onglet "Paramètres" pour calculer les distances.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card>
                <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs">Année</Label>
                    <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
                      <SelectTrigger className="w-28" data-testid="tb-year"><SelectValue /></SelectTrigger>
                      <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Mois</Label>
                    <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
                      <SelectTrigger className="w-36" data-testid="tb-month"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS_FR.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Salarié</Label>
                    <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                      <SelectTrigger className="w-56" data-testid="tb-employee"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous les salariés</SelectItem>
                        {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-sm text-muted-foreground ml-2">
                    Siège : <span className="font-medium text-foreground">{settings.hq_city} ({settings.hq_postal_code})</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    disabled={!compute || compute.total_count === 0}
                    onClick={() => {
                      const fname = exportTravelBonusPDF(compute, {
                        year, month,
                        employeeId: employeeFilter !== "all" ? employeeFilter : undefined
                      });
                      if (fname) toast.success("PDF exporté");
                      else toast.error("Aucune donnée à exporter");
                    }}
                    data-testid="tb-export-pdf"
                  >
                    <FileDown className="h-4 w-4 mr-1" /> Export PDF
                  </Button>
                </CardContent>
              </Card>

              {computeLoading && <div className="flex items-center justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

              {compute && !computeLoading && (
                <>
                  {/* KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Primes générées</div><div className="text-2xl font-bold">{compute.total_count}</div></CardContent></Card>
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Montant total</div><div className="text-2xl font-bold text-primary">{formatCurrency(compute.total_amount)}</div></CardContent></Card>
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Salariés concernés</div><div className="text-2xl font-bold">{compute.by_employee.length}</div></CardContent></Card>
                    <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Période</div><div className="text-lg font-bold">{MONTHS_FR[month]} {year}</div></CardContent></Card>
                  </div>

                  {/* Répartition par zone */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Répartition par zone</CardTitle></CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Zone</TableHead>
                            <TableHead className="text-right">Nombre de primes</TableHead>
                            <TableHead className="text-right">Montant total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[1, 2, 3, 4, 5].map(z => (
                            <TableRow key={z}>
                              <TableCell className="font-medium">{ZONE_LABELS[z]}</TableCell>
                              <TableCell className="text-right font-mono">{compute.by_zone[z]?.count || 0}</TableCell>
                              <TableCell className="text-right font-mono font-bold">{formatCurrency(compute.by_zone[z]?.amount || 0)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50">
                            <TableCell className="font-bold">Total</TableCell>
                            <TableCell className="text-right font-mono font-bold">{compute.total_count}</TableCell>
                            <TableCell className="text-right font-mono font-bold text-primary">{formatCurrency(compute.total_amount)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Par salarié */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Total par salarié</CardTitle></CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      {compute.by_employee.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8 text-sm">Aucune prime pour cette période</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Salarié</TableHead>
                              <TableHead className="text-right">Nb primes</TableHead>
                              <TableHead className="text-right">Zone 1</TableHead>
                              <TableHead className="text-right">Zone 2</TableHead>
                              <TableHead className="text-right">Zone 3</TableHead>
                              <TableHead className="text-right">Zone 4</TableHead>
                              <TableHead className="text-right">Zone 5</TableHead>
                              <TableHead className="text-right">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {compute.by_employee.map(e => (
                              <TableRow key={e.employee_id}>
                                <TableCell className="font-medium">{e.employee_name}</TableCell>
                                <TableCell className="text-right font-mono">{e.count}</TableCell>
                                {[1, 2, 3, 4, 5].map(z => (
                                  <TableCell key={z} className="text-right font-mono text-xs">
                                    {e.by_zone[String(z)]?.count || 0} / {formatCurrency(e.by_zone[String(z)]?.amount || 0)}
                                  </TableCell>
                                ))}
                                <TableCell className="text-right font-mono font-bold text-primary">{formatCurrency(e.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>

                  {/* Détail jour par jour */}
                  <Card>
                    <CardHeader><CardTitle className="text-base">Détail jour par jour</CardTitle></CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      {compute.per_day.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8 text-sm">Aucune prime</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Salarié</TableHead>
                              <TableHead>Commande</TableHead>
                              <TableHead>Ville chantier</TableHead>
                              <TableHead className="text-right">Distance</TableHead>
                              <TableHead className="text-center">Zone</TableHead>
                              <TableHead className="text-right">Montant</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {compute.per_day.map((d, i) => (
                              <TableRow key={`${d.date}-${d.employee_id}-${i}`}>
                                <TableCell className="font-mono text-xs">{d.date}</TableCell>
                                <TableCell>{d.employee_name}</TableCell>
                                <TableCell className="font-mono text-xs">{d.order_reference}</TableCell>
                                <TableCell className="text-sm">{d.city}</TableCell>
                                <TableCell className="text-right font-mono">{d.distance_km != null ? `${d.distance_km} km` : "-"}</TableCell>
                                <TableCell className="text-center">
                                  {d.zone ? <Badge variant="outline">Zone {d.zone}</Badge> : d.error ? <Badge variant="destructive" className="text-[10px]">{d.error}</Badge> : "-"}
                                </TableCell>
                                <TableCell className="text-right font-mono font-bold">{formatCurrency(d.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* ---------- SETTINGS ---------- */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Siège social
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 max-w-xl">
              <div className="space-y-2">
                <Label>Ville du siège *</Label>
                <CityAutocomplete value={hqCity} onChange={setHqCity} placeholder="Saisissez votre ville ou code postal" data-testid="hq-city-input" />
                <p className="text-xs text-muted-foreground">Sert à calculer la distance à vol d'oiseau avec le lieu de chaque chantier.</p>
              </div>
              {settings?.hq_latitude && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" /> Coordonnées : {settings.hq_latitude.toFixed(4)}, {settings.hq_longitude.toFixed(4)}
                </div>
              )}
              <Button onClick={handleSaveSettings} data-testid="save-hq"><Save className="h-4 w-4 mr-2" /> Enregistrer</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- RATES ---------- */}
        <TabsContent value="rates" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-bold text-lg">Grilles tarifaires</h3>
              <p className="text-sm text-muted-foreground">Définissez les montants par zone, avec historique (date d'effet). Vous pouvez créer une grille par défaut ou une grille spécifique à un salarié.</p>
            </div>
            <Button onClick={() => openRateDialog()} data-testid="add-rate"><Plus className="h-4 w-4 mr-2" /> Nouveau tarif</Button>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Grilles par défaut (tous les salariés)</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {defaultRates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">Aucune grille par défaut. Cliquez sur "Nouveau tarif".</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date d'effet</TableHead>
                      <TableHead className="text-right">Zone 1</TableHead>
                      <TableHead className="text-right">Zone 2</TableHead>
                      <TableHead className="text-right">Zone 3</TableHead>
                      <TableHead className="text-right">Zone 4</TableHead>
                      <TableHead className="text-right">Zone 5</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {defaultRates.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono">{r.start_date}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(r.zone1)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(r.zone2)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(r.zone3)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(r.zone4)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(r.zone5)}</TableCell>
                        <TableCell><Button variant="ghost" size="icon" onClick={() => handleDeleteRate(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Grilles spécifiques par salarié</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {employeeRates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">Aucune grille personnalisée. Les salariés utilisent le tarif par défaut.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Salarié</TableHead>
                      <TableHead>Date d'effet</TableHead>
                      <TableHead className="text-right">Zone 1</TableHead>
                      <TableHead className="text-right">Zone 2</TableHead>
                      <TableHead className="text-right">Zone 3</TableHead>
                      <TableHead className="text-right">Zone 4</TableHead>
                      <TableHead className="text-right">Zone 5</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employeeRates.map(r => {
                      const emp = employees.find(e => e.id === r.employee_id);
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{emp ? `${emp.first_name} ${emp.last_name}` : r.employee_id}</TableCell>
                          <TableCell className="font-mono">{r.start_date}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.zone1)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.zone2)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.zone3)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.zone4)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(r.zone5)}</TableCell>
                          <TableCell><Button variant="ghost" size="icon" onClick={() => handleDeleteRate(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rate dialog */}
      <Dialog open={rateDialog} onOpenChange={setRateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouveau tarif</DialogTitle>
            <DialogDescription>Prend effet à partir de la date indiquée. Les tarifs précédents restent appliqués aux dates antérieures.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date d'effet *</Label>
                <Input type="date" value={rateForm.start_date} onChange={e => setRateForm({ ...rateForm, start_date: e.target.value })} data-testid="rate-start-date" />
              </div>
              <div>
                <Label>Salarié (optionnel)</Label>
                <Select value={rateForm.employee_id || "default"} onValueChange={v => setRateForm({ ...rateForm, employee_id: v === "default" ? "" : v })}>
                  <SelectTrigger data-testid="rate-employee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Tarif par défaut (tous)</SelectItem>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 pt-2">
              {[1, 2, 3, 4, 5].map(z => (
                <div key={z}>
                  <Label className="text-xs">Zone {z} (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={rateForm[`zone${z}`]}
                    onChange={e => setRateForm({ ...rateForm, [`zone${z}`]: e.target.value })}
                    data-testid={`rate-zone${z}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-xs text-muted-foreground pt-2">
              Rappel : zone 1 = 0-10 km, zone 2 = 10-20 km, ... zone 5 = 40-50 km. Au-delà de 50 km, on repart en zone 1.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateRate} data-testid="rate-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TravelBonusTab;
